<template>
	<div class="wrapper">
		<div class="wrapper__img-list">
			<label class="wrapper__img-list-item wrapper__img-selector">
				<input
					v-show="false"
					ref="inputElem"
					type="file"
					accept="image/*"
					:disabled="disabled"
					multiple
					@change="inputChange"
				/>
				<span
					:class="[
						'wrapper__img-selector-ui',
						{ 'wrapper__img-selector-ui--disabled': disabled },
					]"
				/>
			</label>
			<SlickList
				v-show="value.length > 0"
				:value="value"
				axis="x"
				class="wrapper__img-slick"
				@input="onInput"
			>
				<SlickItem
					v-for="(item, idx) in value"
					:key="`${getSrc(item) + idx}`"
					:index="idx"
					class="wrapper__img-list-item"
				>
					<figure
						class="wrapper__img"
						:style="{ backgroundImage: `url('${getSrc(item)}')` }"
					>
						<span
							v-if="(maxLength && maxLength > 1) || !maxLength"
							class="wrapper__img-label"
							v-text="idx + 1"
						/>
						<button
							class="wrapper__img-delete-button"
							@click="deleteImg(idx)"
						/>
					</figure>
				</SlickItem>
			</SlickList>
		</div>
		<ul v-if="useLink">
			<li
				v-for="(item, idx) in value"
				:key="idx"
				class="wrapper__link-item"
			>
				<span class="wrapper__link-label">{{ idx + 1 }} URL :</span>
				<input
					v-model="item.link"
					readonly
					type="text"
					class="wrapper__link-input"
				/>
				<button
					class="btn wrapper__link-set-button"
					@click="openSetLinkModal(idx)"
					v-text="'링크설정'"
				/>
			</li>
		</ul>
	</div>
</template>
<script>
	import { SlickList, SlickItem } from 'vue-slicksort';

	export default {
		name: 'ImgListSelector',
		components: {
			SlickList,
			SlickItem,
		},
		props: {
			value: {
				type: Array,
				default: () => [],
			},
			maxLength: {
				type: Number,
				default: null,
			},
			validation: {
				type: Object,
				default: () => ({}),
			},
			useLink: {
				type: Boolean,
				default: true,
			},
			fixFirstImgRatio: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				firstImgRatio: '',
			};
		},
		computed: {
			disabled() {
				const maxLength = this.maxLength;
				return (
					typeof maxLength === 'number' &&
					maxLength <= this.value.length
				);
			},
		},
		methods: {
			readFile(file) {
				return new Promise(resolve => {
					const reader = new FileReader();
					reader.onload = e => {
						resolve(e);
					};
					reader.readAsDataURL(file);
				});
			},
			getImgData(r) {
				return new Promise(resolve => {
					const img = new Image();
					img.onload = () => {
						resolve(img);
					};
					img.src = r;
				});
			},
			cancelImg() {
				this.$refs.inputElem.value = null;
			},
			validate(img) {
				const { type, value } = this.validation;
				if (
					type === 'tile' ||
					type === 'ratio' ||
					(this.fixFirstImgRatio && this.firstImgRatio)
				) {
					const [widthRatio, heightRatio] = (
						this.firstImgRatio || value
					)
						.split(':')
						.map(item => Number(item));
					const defaultRatio = widthRatio / heightRatio;
					const ratio = img.naturalWidth / img.naturalHeight;
					if (defaultRatio !== ratio) {
						if (this.fixFirstImgRatio) {
							alert('이미지 규격이 같지 않습니다');
						} else {
							alert(`${value} 비율이 아닙니다.`);
						}
						return false;
					}
				}
				if (this.fixFirstImgRatio && !this.firstImgRatio) {
					this.firstImgRatio = `${img.naturalWidth}:${img.naturalHeight}`;
				}
				return true;
			},
			addImgList(urlList, files) {
				const list = urlList.map((url, idx) => {
					const srcObj = {
						src: url,
						fileObject: files[idx],
						extension: files[idx].type.split('/').pop(),
					};
					return this.useLink
						? {
								src: srcObj,
								link: '',
							}
						: srcObj;
				});
				this.$emit('input', [...this.value, ...list]);
			},
			async inputChange(e) {
				const files = [...e.target.files];
				const maxLength = this.maxLength;

				if (!files.length) {
					this.cancelImg();
					return false;
				} else if (
					maxLength &&
					(maxLength < files.length ||
						maxLength + this.value.length < files.length)
				) {
					alert(`쵀대 ${maxLength}까지만 등록 가능합니다.`);
					this.cancelImg();
					return false;
				}

				const readFileList = await Promise.all(
					files.map(file => this.readFile(file))
				);
				const urlList = readFileList.map(({ target }) => target.result);
				const imgList = await Promise.all(
					urlList.map(url => this.getImgData(url))
				);
				this.cancelImg();
				if (imgList.some(img => !this.validate(img))) {
					if (!this.value.length) this.firstImgRatio = '';
					return;
				}
				this.addImgList(urlList, files);
			},
			getSrc(item) {
				const src = this.useLink ? item.src : item;
				return typeof src === 'string' ? src : src.src;
			},
			deleteImg(idx) {
				const newList = [...this.value];
				newList.splice(idx, 1);
				if (!newList.length) this.firstImgRatio = '';
				this.$emit('input', newList);
			},
			setLink(i) {
				return (link, activeIcon) => {
					const newList = [...this.value];
					newList[i].link = link;
					newList[i].activeIcon = activeIcon;
					this.$emit('input', newList);
				};
			},
			openSetLinkModal(i) {
				this.$store.commit('modal/toggleModal', {
					id: 'SetLink',
					params: {
						setLink: this.setLink(i),
					},
				});
			},
			init() {
				this.firstImgRatio = '';
				this.$emit('input', []);
				this.cancelImg();
			},
			onInput(newList) {
				this.$emit('input', newList);
			},
		},
	};
</script>
<style lang="scss" scoped>
	.wrapper {
		width: 100%;

		&__img-list {
			overflow-x: auto;
			display: flex;
			width: 100%;
			flex-wrap: nowrap;
		}

		&__img-list-item {
			display: block;
			width: 100px;
			height: 100px;
			flex: 0 0 auto;
		}

		&__img-slick {
			overflow-x: auto;
			display: flex;
		}

		&__img-selector {
			margin-right: 8px;
		}

		&__img-selector-ui {
			cursor: pointer;
			position: relative;
			display: block;
			width: 100%;
			height: 100%;
			border-radius: 12px;
			border: solid 1px $btn-blue;

			&:after {
				content: '';
				position: absolute;
				display: block;
				width: 24px;
				height: 24px;
				border: 1px solid $btn-blue;
				border-radius: 50%;
				left: 50%;
				top: 50%;
				transform: translate3d(-50%, -50%, 0);
			}

			&:before {
				content: '';
				position: absolute;
				display: block;
				width: 3px;
				height: 3px;
				background: $btn-blue;
				left: 50%;
				top: 50%;
				transform: translate3d(-50%, -50%, 0);
				box-shadow:
					0 3px 0 0 $btn-blue,
					3px 0 0 $btn-blue,
					0 -3px 0 0 $btn-blue,
					-3px 0 0 0 $btn-blue;
			}

			&--disabled {
				background-color: rgba(68, 68, 68, 0.6);
			}
		}

		&__img {
			overflow: hidden;
			position: relative;
			display: block;
			width: 100%;
			height: 100%;
			border-radius: 12px;
			background: no-repeat center/contain;
		}

		&__img-label {
			display: block;
			position: absolute;
			top: 0;
			left: 0;
			width: 24px;
			height: 24px;
			background-color: rgba(68, 68, 68, 0.6);
			line-height: 24px;
			text-align: center;
			color: #fff;
			font-size: 13px;
			font-weight: 700;
		}

		&__img-delete-button {
			display: block;
			position: absolute;
			top: 0;
			right: 0;
			width: 24px;
			height: 24px;
			background: no-repeat center/20px 20px
				url('/static/img/icon/delete--img.svg');
		}

		&__link-item {
			margin-top: 12px;
			display: flex;
			align-items: center;
		}

		&__link-label {
			display: block;
			height: 24px;
			padding: 0 8px;
			flex-shrink: 0;
			background-color: rgba(68, 68, 68, 0.6);
			line-height: 24px;
			text-align: center;
			color: #fff;
			font-size: 13px;
			font-weight: 700;
		}

		&__link-input {
			display: block;
			width: 100%;
			margin-left: 12px;
			border-top-right-radius: 0 !important;
			border-bottom-right-radius: 0 !important;
		}

		&__link-set-button {
			height: 36px;
			border-radius: 4px;
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
		}
	}
</style>
